import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../config/api';
import { getToken, getUser, verifyToken, clearAuth, login as authLogin } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

// Only this role may use the back office; the API answers 403 for every other role.
export const ADMIN_ROLE = 'admin';
export const hasAdminAccess = (user) => String(user?.role || '').toLowerCase() === ADMIN_ROLE;

export const NO_PERMISSION_MESSAGE = 'You do not have permission to access this area.';

const ROLE_LABELS = { admin: 'Administrator', editor: 'Editor', viewer: 'Viewer' };
const roleName = (role) => {
  const r = String(role || '').toLowerCase();
  return ROLE_LABELS[r] || (r ? r.charAt(0).toUpperCase() + r.slice(1) : 'Unknown');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Set when the API rejects a request with 403 (role without permission). The session
  // is kept; the UI shows a "no permission" state instead of empty pages.
  const [forbidden, setForbidden] = useState(null);
  const retryTimerRef = useRef(null);

  const clearForbidden = useCallback(() => setForbidden(null), []);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const token = getToken();
      if (token) {
        try {
          const userData = await verifyToken();
          setUser(userData);
          setIsAuthenticated(true);
        } catch (error) {
          if (error && error.transient) {
            // 429 / network: keep the stored session and re-verify later.
            console.warn('Token verification postponed:', error.message);
            const stored = getUser();
            if (stored) {
              setUser(stored);
              setIsAuthenticated(true);
            }
            const delayMs = Math.min(15, Math.max(1, error.retryAfterMinutes || 1)) * 60 * 1000;
            retryTimerRef.current = setTimeout(() => { checkAuth(); }, delayMs);
          } else {
            console.error('Token verification failed:', error);
            clearAuth();
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  // Central 403 handling for every admin API call: never log the user out, just
  // surface a permission-denied state with the server's message.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 403 && getToken()) {
          const data = error.response.data;
          const message = (data && typeof data === 'object' && data.error) || NO_PERMISSION_MESSAGE;
          setForbidden({ message, at: Date.now() });
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const login = async (username, password) => {
    const { user: userData } = await authLogin(username, password);
    if (!hasAdminAccess(userData)) {
      clearAuth();
      throw Object.assign(
        new Error(`${NO_PERMISSION_MESSAGE} Your account role is "${roleName(userData?.role)}"; an Administrator account is required.`),
        { status: 403 }
      );
    }
    setForbidden(null);
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  };

  const logout = () => {
    clearAuth();
    setUser(null);
    setIsAuthenticated(false);
    setForbidden(null);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin: hasAdminAccess(user),
    forbidden,
    clearForbidden,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
